import type { FragmentType } from 'src/gql';
import type { AssetFields } from 'src/types/asset';
import { Text } from '@savvy-kit/foundations/text';
import InputField from '@savvy-kit/patterns/input-field';
import { useFormik } from 'formik';
import MonetaryInputTextField from 'src/components/mui/monetary-input-text-field';
import TrackedButton from 'src/components/tracked/tracked-button';
import { MAX_STRING_LENGTH } from 'src/constants/database';
import { graphql, unmaskFragment } from 'src/gql';
import { number as yupNumber, object as yupObject, string as yupString } from 'yup';

export const CompanyEquityBasicsFormFragment = graphql(/* GraphQL */ `
  fragment CompanyEquityBasicsForm on Asset {
    companyEquityCompanyName
    companyEquityCompanyTicker
    companyEquityCurrentPrice
  }
`);

export default function CompanyEquityBasicsForm({
  assetFrag,
  assetFields,
  isEditMode,
  onNext,
}: {
  readonly assetFrag?: FragmentType<typeof CompanyEquityBasicsFormFragment>;
  readonly assetFields?: AssetFields;
  readonly isEditMode: boolean;
  readonly onNext: (assetFields?: AssetFields) => void;
}) {
  const asset = unmaskFragment(CompanyEquityBasicsFormFragment, assetFrag);
  const formik = useFormik({
    initialValues: {
      companyEquityCompanyName: assetFields?.companyEquityCompanyName ?? asset?.companyEquityCompanyName ?? '',
      companyEquityCompanyTicker: assetFields?.companyEquityCompanyTicker ?? asset?.companyEquityCompanyTicker ?? '',
      companyEquityCurrentPrice:
        (assetFields?.companyEquityCurrentPrice ?? asset?.companyEquityCurrentPrice)
          ? (
              Math.round(Number(assetFields?.companyEquityCurrentPrice ?? asset?.companyEquityCurrentPrice) * 100) / 100
            ).toFixed(2)
          : '',
    },
    validationSchema: yupObject({
      companyEquityCompanyName: yupString()
        .max(MAX_STRING_LENGTH, `Company name must be at most ${MAX_STRING_LENGTH} characters`)
        .required('Company name is required'),
      companyEquityCompanyTicker: yupString().required('Company ticker is required'),
      companyEquityCurrentPrice: yupNumber().required().positive().required('Current price is required'),
    }),
    onSubmit: async () => await Promise.resolve(),
  });

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <Text variant="body-sm" color="secondary">
        We support public company equity at the moment. If you’d like to add private company equity, use the ‘Other
        asset’ type.
      </Text>
      <InputField
        id="companyEquityCompanyName"
        containerClassName="mt-4 mb-2"
        error={Boolean(formik.touched.companyEquityCompanyName && formik.errors.companyEquityCompanyName)}
        errorMessage={formik.errors.companyEquityCompanyName}
        label="Company name"
        name="companyEquityCompanyName"
        onBlur={formik.handleBlur}
        onChange={formik.handleChange}
        type="text"
        value={formik.values.companyEquityCompanyName}
      />
      <InputField
        id="companyEquityCompanyTicker"
        containerClassName="mt-4 mb-2"
        error={Boolean(formik.touched.companyEquityCompanyTicker && formik.errors.companyEquityCompanyTicker)}
        errorMessage={formik.errors.companyEquityCompanyTicker}
        description="The short name for the company stock. Usually one to five letters. For example, GOOGL or AAPL."
        label="Company ticker"
        name="companyEquityCompanyTicker"
        onBlur={formik.handleBlur}
        onChange={(e) => {
          const value = e.target.value;
          const cleanValue = value.replace(/[^a-zA-Z]/g, '').toUpperCase();
          formik.setFieldValue('companyEquityCompanyTicker', cleanValue);
        }}
        type="text"
        value={formik.values.companyEquityCompanyTicker}
      />

      <MonetaryInputTextField
        error={Boolean(formik.touched.companyEquityCurrentPrice && formik.errors.companyEquityCurrentPrice)}
        errorMessage={formik.errors.companyEquityCurrentPrice}
        label="Current price"
        name="companyEquityCurrentPrice"
        onKeyUp={(e) => {
          if (e.keyCode === 13) {
            setTimeout(() => {
              onNext({ ...formik.values });
            });
          }
        }}
        formik={formik}
        value={formik.values.companyEquityCurrentPrice}
      />
      <div className="flex flex-row justify-end mt-4 mb-6">
        <TrackedButton
          elementName={
            isEditMode
              ? 'update-company-equity-basics-form-next-button'
              : 'create-company-equity-basics-form-next-button'
          }
          onClick={() => {
            onNext({ ...formik.values });
          }}
          disabled={!isEditMode && (formik.isSubmitting || !formik.isValid)}
        >
          Next
        </TrackedButton>
      </div>
    </form>
  );
}
